import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslate } from "helpers";
import {
  deleteFleetVehicleDamageAPI,
  getFleetVehicleDamagesAPI,
} from "services/api/fleet/vehicles/damages/api";
import { getFleetVehiclePicturesAPI } from "services/api/fleet/vehicles/pictures/api";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  FleetVehicleDamage,
  FleetVehicleDamageRow,
} from "services/api/fleet/vehicles/damages/types";
import {
  VehicleDamageSearchPageCmp,
  VehicleDamageSearchPageProps,
} from "./types";
import { FLEET_VEHICLE_DAMAGE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { Button } from "ui/components/Button/Button";
import { NoResults } from "ui/components/NoResults";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";
import TabMenu from "ui/modules/fleet/vehicles/pages/components/TabMenu/TabMenu";
import { VehicleTabHeader } from "ui/modules/fleet/vehicles/pages/components/VehicleTabHeader";
import VehicleDamageSearchResults from "./components/VehicleDamageSearchResults/VehicleDamageSearchResults";
import VehicleDamageSearchFilters from "./components/VehicleDamageSearchFilters/VehicleDamageSearchFilters";
import DamagePinOverlay, {
  DamagePin,
} from "./components/DamagePinOverlay/DamagePinOverlay";
import VehicleDamageEditCard from "../VehicleDamageEditCard/VehicleDamageEditCard";
import VehicleStatusFlow from "ui/modules/fleet/vehicles/pages/components/VehicleStatusEditCard/components/VehicleStatusFlow/VehicleStatusFlow";
import { Dialog } from "ui/components/Dialog";
import { FleetVehicleDetailFormValues, FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { initialFleetVehicleDamageSearchForm } from "services/api/fleet/vehicles/damages/consts";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { FleetVehiclePictureRow } from "services/api/fleet/vehicles/pictures/types";
import { initialPagination } from "ui/components/Table/consts";

const STATUS_PIN_COLORS: Record<string, string> = {
  orange: "#f97316",
  blue: "#3b82f6",
  green: "#22c55e",
  slate: "#64748b",
};

const VehicleDamageSearchPage: VehicleDamageSearchPageCmp = (
  props: VehicleDamageSearchPageProps
) => {
  const { id } = useParams();
  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleDamageRow>
  >(initialDataWithPagination);
  const [vehicleImages, setVehicleImages] = useState<
    { id: string; url: string }[]
  >([]);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [damageId, setDamageId] = useState<string>("");
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activePin, setActivePin] = useState<string | null>(null);
  const [wizardStep, setWizardStep] = useState(0);
  // Ponuda promjene statusa nakon unosa nove štete.
  const [showUnavailableDialog, setShowUnavailableDialog] = useState<boolean>(false);
  const [showStatusFlow, setShowStatusFlow] = useState<boolean>(false);
  const [unavailableDamageId, setUnavailableDamageId] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchVehicle(id);
      fetchSearchResult(id, searchResults.pagination);
      fetchVehicleImages(id);
    }
  }, [id]);

  const fetchVehicle = async (id: string) => {
    if (!id) {
      setFormValues(initialFleetVehicleDetailForm);
      return;
    }

    setIsLoading(true);
    try {
      const result = await getFleetVehicleAPI(id);
      setFormValues(result);
    } catch {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const fetchVehicleImages = async (vehicleId: string) => {
    try {
      const result = await getFleetVehiclePicturesAPI(vehicleId, {
        ...initialPagination,
        pageSize: 100,
      });
      const images = result.data.map((img: FleetVehiclePictureRow) => ({
        id: img.id,
        url: img.url,
      }));
      setVehicleImages(images);
      if (images.length > 0) {
        setSelectedImageId(images[0].id);
      }
    } catch {
      // ignore
    }
  };

  const fetchSearchResult = async (id: string, pagination: Pagination) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleDamagesAPI(pagination, {
        ...initialFleetVehicleDamageSearchForm,
        vehicleId: id,
      });
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setDamageId(id);
    setWizardStep(0);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setDamageId("");
      setWizardStep(0);
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(
    (saved?: FleetVehicleDamage) => {
      if (id) fetchSearchResult(id, searchResults.pagination);

      // Nova šteta ne mora značiti da vozilo stoji — većina su ogrebotine — pa se
      // promjena statusa nudi, a ne radi sama od sebe.
      const isNew = !damageId;
      setDamageId("");
      setShowAddNewModal(false);

      if (isNew && saved?.id && formValues.status !== FleetVehicleStatusEnum.UNAVAILABLE_DAMAGE) {
        setUnavailableDamageId(saved.id);
        setShowUnavailableDialog(true);
      }
    },
    [id, damageId, searchResults.pagination, formValues.status]
  );

  const handleUnavailableConfirm = useCallback(() => {
    setShowUnavailableDialog(false);
    setShowStatusFlow(true);
  }, []);

  const handleUnavailableCancel = useCallback(() => {
    setShowUnavailableDialog(false);
    setUnavailableDamageId(null);
  }, []);

  const handleStatusFlowClose = useCallback(() => {
    setShowStatusFlow(false);
    setUnavailableDamageId(null);
  }, []);

  const handleStatusSaved = useCallback(() => {
    if (id) fetchVehicle(id);
  }, [id]);

  const handleSearchChange = useCallback(() => {
    if (id) fetchSearchResult(id, searchResults.pagination);
  }, [id, searchResults.pagination]);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id) fetchSearchResult(id, newPagination);
  };

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setDamageId(id);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!damageId) return;

    try {
      const deleted = await deleteFleetVehicleDamageAPI(damageId, t);
      if (deleted && id) {
        await fetchSearchResult(id, searchResults.pagination);
      }
      setOpenDialog(false);
      setDamageId("");
    } catch (err) {
      console.error("Error deleting damage claim:", err);
    }
  }, [damageId, t, id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setDamageId("");
    setOpenDialog(false);
  }, []);

  const handlePinClick = (pinId: string) => {
    setActivePin(pinId === activePin ? null : pinId);
  };

  // Build pins for the selected image from search results (using claim.pins array)
  const selectedImage = vehicleImages.find(
    (img) => img.id === selectedImageId
  );

  const pinsForImage: DamagePin[] = searchResults.data
    .flatMap((claim) =>
      (claim.pins ?? [])
        .filter((pin) => pin.uploadId === selectedImageId)
        .map((pin) => ({
          id: pin.id,
          x: pin.pinX,
          y: pin.pinY,
          color:
            STATUS_PIN_COLORS[pin.vehicleDamageStatusColor ?? "orange"] ??
            "#f97316",
          label: pin.name,
          statusName: pin.vehicleDamageStatusName,
          description: pin.description,
        }))
    );

  if (!id) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <VehicleTabHeader
            vehicleId={id}
            vehicle={formValues}
            title={t(FLEET_VEHICLE_DAMAGE_SEARCH_PAGE_TITLE.text)}
            icon={FLEET_VEHICLE_DAMAGE_SEARCH_PAGE_TITLE.icon}
            iconColor={FLEET_VEHICLE_DAMAGE_SEARCH_PAGE_TITLE.iconColor}
            onStatusChange={handleStatusSaved}
          >
            {/* Prodano vozilo je arhivirano — postojeće štete ostaju, nove se ne dodaju. */}
            {formValues.status !== FleetVehicleStatusEnum.SOLD && (
              <Button
                variant="primary"
                children={t("admin.fleet.vehicle.damage.search.add")}
                startIcon="/media/icons/plus.svg"
                onClick={handleAddNew}
              />
            )}
          </VehicleTabHeader>
        </div>
        <TabMenu vehicleId={id} />
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          {/* Pin overlay section */}
          {vehicleImages.length > 0 && (
            <div className="mb-6">
              <div className="flex gap-2 mb-3 flex-wrap">
                {vehicleImages.map((img) => (
                  <div
                    key={img.id}
                    onClick={() => setSelectedImageId(img.id)}
                    className={`w-16 h-16 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${selectedImageId === img.id
                        ? "border-sky-600 ring-2 ring-sky-200"
                        : "border-slate-200 hover:border-slate-400"
                      }`}
                  >
                    <img
                      src={img.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              {selectedImage && (
                <div className="max-w-2xl">
                  <DamagePinOverlay
                    imageUrl={selectedImage.url}
                    pins={pinsForImage}
                    onPinClick={handlePinClick}
                    activePin={activePin}
                  />
                </div>
              )}
            </div>
          )}

          {/* Filters + Results */}
          <VehicleDamageSearchFilters onSearchChange={handleSearchChange} />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t("admin.fleet.vehicle.damage.noresults")}
            />
          ) : (
            <VehicleDamageSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t("admin.fleet.vehicle.damage.dialog.delete.title")}
        message={t("admin.fleet.vehicle.damage.dialog.delete.message")}
        confirmText={t(
          "admin.fleet.vehicle.damage.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.damage.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteDialogClose}
      />
      {showAddNewModal && (
        <VehicleDamageEditCard
          show={showAddNewModal}
          vehicleId={id}
          damageId={damageId}
          onSuccess={handleEditSuccess}
          onClose={() => setShowAddNewModal(false)}
          onStepChange={setWizardStep}
        />
      )}

      <Dialog
        open={showUnavailableDialog}
        title="Je li vozilo nedostupno?"
        message="Šteta je spremljena. Ako vozilo zbog nje ne može biti u prometu, status se može odmah promijeniti."
        confirmText="Promijeni status"
        cancelText="Ne, hvala"
        onConfirm={handleUnavailableConfirm}
        onCancel={handleUnavailableCancel}
      />

      {/* Šteta je već odabrana — modal traži još samo rok i napomenu. */}
      <VehicleStatusFlow
        show={showStatusFlow}
        vehicleId={id}
        currentStatus={
          (formValues.status as FleetVehicleStatusEnum) ?? FleetVehicleStatusEnum.AVAILABLE
        }
        status={
          showStatusFlow ? FleetVehicleStatusEnum.UNAVAILABLE_DAMAGE : null
        }
        prefill={{ vehicleDamageId: unavailableDamageId }}
        onClose={handleStatusFlowClose}
        onSaved={handleStatusSaved}
      />
    </>
  );
};

VehicleDamageSearchPage.route =
  FleetVehicleRoutes.FleetVehicleDamageSearchPageRouter;

export default VehicleDamageSearchPage;
